import React, { useEffect, useState } from 'react';
import { Form, Input, Flex, message } from 'antd';
import { CustomButton } from '@/components';
import { initiateTestAiCall } from '../../../../apis/shipment';
import { useToken } from '@/common/helper';
import AiTestCallModal from './AiTestCall';
import { useSpin } from '../../../../components/SpinContext';
import { PhoneIcon, MailIcon, PencilIcon } from '@/assets/icons/icons';
import EmailPreview from './EmailPreview';
import { SingleShipmentData } from '@/apis/types';

type AiNotificationMethod = 'AI_EMAIL' | 'AI_CALL';

interface OneTimeMessageBoxProps {
  notifyVia?: AiNotificationMethod | string;
  shipment?: SingleShipmentData;
}

/**
 * Component for composing and previewing one-time notification messages
 *
 * @param notifyVia - Notification method (AI_CALL or AI_EMAIL)
 * @param shipment - Shipment data including contact information
 */
const OneTimeMessageBox: React.FC<OneTimeMessageBoxProps> = ({
  notifyVia,
  shipment,
}) => {
  const { showSpin, hideSpin } = useSpin();
  const { decodedUserToken, getUserID } = useToken();
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [showEmailPreview, setShowEmailPreview] = useState<boolean>(false);
  const [customMessage, setCustomMessage] = useState<string>('');
  const decodedToken = decodedUserToken();

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>): void => {
    setCustomMessage(e.target.value);
  };

  const handleAiCallConfirm = (): void => {
    const aiCallPayload = {
      contact_name: decodedToken?.name,
      company_name: decodedToken?.company_name,
      user_notification:
        'This is a test call to preview the AI notification message content.',
      test_phone_number: decodedToken?.phone,
      triggered_by: getUserID() ?? undefined,
      company_id: decodedToken?.company_id,
    };
    showSpin();
    initiateTestAiCall(aiCallPayload)
      .then((statusRes) => {
        message.success(statusRes?.message);
        setModalOpen(false);
      })
      .catch((error) => {
        message.error(
          error?.data?.message || error.message || 'Something went wrong'
        );
      })
      .finally(() => {
        hideSpin();
      });
  };

  useEffect(() => {
    if (notifyVia !== 'AT_EMAIL') {
      setShowEmailPreview(false);
    }
  }, [notifyVia]);

  return (
    <>
      <div>
        <Flex
          justify='space-between'
          align='center'
          style={{ marginBottom: '4px' }}
        >
          <span className='required font-14 fw-600'>Message</span>
          {notifyVia === 'AI_CALL' && (
            <CustomButton
              iconPosition='left'
              icon={<PhoneIcon />}
              gap={'8px'}
              text='Test AI call'
              isFormValid={decodedToken?.phone === null}
              fontSize='12px'
              fontWeight='600'
              height='28px'
              boxShadow='none'
              bgColor='var(--light-gray)'
              color='var(--text-primary)'
              padding='0 12px'
              paddingImage='5px'
              borderRadius='28px'
              onClick={() => setModalOpen(true)}
            />
          )}

          {notifyVia === 'AI_EMAIL' && (
            <CustomButton
              iconPosition='left'
              icon={
                !showEmailPreview ? (
                  <MailIcon />
                ) : (
                  <PencilIcon
                    width={14}
                    height={14}
                    color='var(--text-primary)'
                  />
                )
              }
              gap={'8px'}
              text={
                !showEmailPreview ? 'AI Email preview' : 'Customize message'
              }
              isFormValid={decodedToken?.phone === null}
              fontSize='12px'
              fontWeight='600'
              height='28px'
              boxShadow='none'
              bgColor='var(--light-gray)'
              color='var(--text-primary)'
              padding='0 12px'
              paddingImage='5px'
              borderRadius='28px'
              onClick={() => setShowEmailPreview((prev) => !prev)}
            />
          )}
        </Flex>
        {!showEmailPreview ? (
          <Form.Item
            name='message'
            rules={[
              { required: true, message: 'Message is required' },
              {
                type: 'string',
                min: 6,
                message: 'Message must be at least 6 characters long.',
              },
              {
                type: 'string',
                max: 300,
                message: 'Message cannot exceed 300 characters.',
              },
            ]}
          >
            <Input.TextArea
              placeholder='Enter your message here...'
              rows={4}
              className='onetime-text-area'
              onChange={handleChange}
            />
          </Form.Item>
        ) : (
          <EmailPreview
            shipment={shipment}
            customMessage={customMessage}
            decodedToken={decodedToken ?? undefined}
          />
        )}
      </div>
      <AiTestCallModal
        modalOpen={modalOpen}
        setModalOpen={setModalOpen}
        phone={decodedToken?.phone || ''}
        handleAiCallConfirm={handleAiCallConfirm}
      />
    </>
  );
};

export default OneTimeMessageBox;
